import type { ReactNode } from "react";
import { usePage } from "@inertiajs/react";
import { ServerMetaHead } from "@/components/shared/ServerMetaHead";
import { ContactWidget } from "@/components/shared/ContactWidget";
import { ExitIntentNewsletter } from "@/components/shared/ExitIntentNewsletter";
import { useReveal } from "./useReveal";

/**
 * The shell every redesigned page renders inside.
 *
 * It deliberately does NOT reuse `@/components/Layout` — that one carries the
 * old design system's header, rebate banner and fixed mobile booking bar. Each
 * Bluebird page renders its own masthead and the same complete shared footer.
 *
 * The `bluebird` class is load-bearing, not decorative: it scopes the
 * content-box reset that the comp's geometry depends on. See
 * app/frontend/styles/bluebird.css.
 */
export function BluebirdLayout({
  children,
}: {
  children: ReactNode;
  title?: string;
}) {
  // KEYED ON THE INERTIA PAGE URL, and that is the whole bug fix.
  //
  // useReveal ran once with an empty dependency array. Inertia keeps the layout
  // MOUNTED across navigations — that is the point of it — so on every
  // client-side page change the effect never re-ran: the new page's
  // [data-reveal] elements were armed to opacity 0 by CSS and then never
  // observed, because the observer was still watching elements from the page
  // the visitor had left.
  //
  // The visible symptom was a page rendered but invisible below the hero, fixed
  // by a manual refresh — which is exactly what a full page load does, remount
  // the layout. Aaron hit it repeatedly and reasonably guessed caching.
  // Reproduced 4 of 4 times navigating to /bikes by link, 0 of 4 by reload.
  //
  // The url changes on every navigation, so the effect tears down its observer
  // and re-arms against the new document.
  const { url } = usePage();
  const pathname = url.split(/[?#]/, 1)[0];
  useReveal(url);
  return (
    <div className="bluebird">
      <ServerMetaHead />
      {children}
      <ExitIntentNewsletter />
      {pathname !== "/book" && <ContactWidget />}
    </div>
  );
}
